import { stripHTML } from "@decocms/apps-website/utils/html"
import { SendEventOnView } from "~/components/Analytics"
import ProductCard, {
	LoadingFallback as ProductCardLoadingFallback,
} from "~/components/product/ProductCard"
import Icon from "~/components/ui/Icon"
import Slider from "~/components/ui/Slider"
import SliderJS from "~/components/ui/SliderJS"
import Title from "~/components/ui/Title"
import { TitleIcon } from "~/loaders/TitleIcon"
import clsx from "~/sdk/clx"
import { isSchedulingActive, type Scheduling } from "~/sdk/contentScheduling"
import { toAnalytics } from "~/sdk/ga4/transform/toAnalytics"
import { toProductItem } from "~/sdk/ga4/transform/toProductItem"
import trimProductForShelf from "~/sdk/trimProductForShelf"
import { useId } from "react"
import type { Product, ViewItemListParams } from "@decocms/apps-commerce/types"

import { checkDisabledBuyMonthlySubscription, MONTHLY_SKU_ID } from "~/sdk/useSubscription"
import { SITE } from "~/constants"

export type FacetType = 'default' | 'category' | 'subcategory'

export interface Props {
	products: Product[] | null
	/**
	 * @title Título
	 * @format html
	 */
		title?: string
	/**
	 * @title Selecione os facets de prateleira na página de produto
	 */
	shelfFacetsType?: FacetType

	
	/**
	 * @title Cor do título
	 * @format color
	 */
	titleColor?: string
	/**
	 * @title Ícone
	 */
	titleIcon: TitleIcon
	/**
	 * @title Parâmetros do GA4
	 */
	ga4?: Omit<ViewItemListParams, 'items'>
	/**
	 * @title Remover slider no desktop
	 */
	removeSliderOnDesktop?: boolean
	/**
	 * @title Remover slider no mobile
	 */
	removeSliderOnMobile?: boolean
	/**
	 * @title Slider infinito
	 * @default true
	 */
	infinite?: boolean
	/**
	 * @title Margem de baixo
	 * @description ex. 100px
	 */
	marginBottom?: string
	/**
	 * @title Margem de baixo no mobile
	 * @description ex. 100px
	 */
	marginBottomMobile?: string
	/**
	 * @title Agendamento
	 * @description Deixe este campo vazio para manter sempre visível
	 */
	scheduling?: Scheduling
}

export const loader = async (
	props: Props & { __pageUrl?: string; isMobile?: boolean },
) => {
	// Trim before serialization: the CMS `products` prop comes from the legacy
	// productList/productListingPage loaders with FULL variants (~289 KB each),
	// and the card reads a fraction of it — see ~/sdk/trimProductForShelf.
	const products = (props.products?.filter(Boolean) ?? []).map(trimProductForShelf)

	const disabledBuyMonthlySubscription = await checkDisabledBuyMonthlySubscription(
		products ?? [],
	)

	const pageUrl = props.__pageUrl ?? "/"
	return {
		...props,
		products,
		isMobile: props.isMobile ?? false,
		isPerformanceTestPage: new URL(pageUrl, SITE.URL).pathname.includes('performance'),
		disabledBuyMonthlySubscription,
	}
	
	// if (products?.length > 0) {
	// }

	// const getFacets = (category: string | undefined, type: FacetType | undefined) => {
	// 	if (!category) return ''

	// 	const formatMultipleFacets = (category: string) =>
	// 		category.split('>').map((item, index) => `category-${index + 1}/${item}`).join('/')

	// 	const formatters = {
	// 		default: formatMultipleFacets,
	// 		category: (category: string) => `category-1/${category.split('>')[0]}`,
	// 		subcategory: formatMultipleFacets,
	// 	}

	// 	return (type ? formatters[type] : formatters['default'])(category)
	// }

	// const fetchProductList = async (count: number, facets: string) => {
	// 	return await ctx.invoke(
	// 		'vtex/loaders/intelligentSearch/productList.ts',
	// 		{ props: { query: '', count, sort: 'orders:desc', facets, hideUnavailableItems: true } },
	// 	)
	// }

	// const slug = new URL(request.url).pathname.split('/')[1]
	// const currentProduct = await ctx.invoke.vtex.loaders.legacy.productDetailsPage({ slug })
	// const category = currentProduct?.product?.category
	// const selectedFacetType = props.shelfFacetsType ?? 'default'
	// const facets = getFacets(category, selectedFacetType)

	// const productsFacet = facets ? await fetchProductList(24, facets) : []

	// const getRandomProducts = (products: Product[], n: number) => {
	// 	if (!products?.length) return []
	// 	const shuffled = [...products].sort(() => Math.random() - 0.5)
	// 	return shuffled.slice(0, n)
	// }

	// const randomProducts = getRandomProducts(productsFacet ?? [], 8)

	// return {
	// 	...props,
	// 	products: randomProducts,
	// 	isMobile: ctx.device !== 'desktop',
	// 	isPerformanceTestPage: new URL(request.url).pathname.includes('performance'),
	// 	disabledBuyMonthlySubscription,
	// }
}

function ProductShelf(
	{
		products,
		title,
		titleIcon,
		isMobile,
		titleColor,
		ga4,
		removeSliderOnDesktop,
		removeSliderOnMobile,
		infinite = true,
		marginBottom,
		marginBottomMobile,
		scheduling,
		isPerformanceTestPage,
		disabledBuyMonthlySubscription,
	}: Awaited<ReturnType<typeof loader>>,
) {
	const id = useId()
	if (!products?.length || !isSchedulingActive(scheduling)) {
		return null
	}

	const viewItemListEvent = toAnalytics({
		type: 'view_item_list',
		data: {
			items: products.map((product, index) => toProductItem(product, { quantity: 1, index })),
			view: {
				id: ga4?.item_list_id ?? 'product_shelf',
				name: ga4?.item_list_name ?? stripHTML(title ?? '') ?? 'Prateleira',
			},
		},
	})

	return (
		<div
			className='w-full flex flex-col gap-12 mx-auto max-w-[1730px] py-6 lg:py-12 px-2 sm:px-4'
			style={{
				marginBottom: isMobile
					? removeSliderOnMobile ? '0' : marginBottomMobile
					: removeSliderOnDesktop
					? '0'
					: marginBottom,
			}}
		>
			<div className='flex items-center justify-center gap-3 px-3'>
				{title && <Title title={title} icon={titleIcon} color={titleColor} />}
			</div>

			{(!removeSliderOnDesktop && !isMobile) || (!removeSliderOnMobile && isMobile)
				? (
					<div id={id} className='flex relative gap-6 w-full justify-center px-[5vw]'>
						<Slider className='carousel gap-7 w-full'>
							{products?.map((product, index) => {
								const shouldDisabled = disabledBuyMonthlySubscription &&
									product.productID === MONTHLY_SKU_ID

								return (
									<Slider.Item
										index={index}
										// Show X carousel items for each resolution
										// calc((100% / var(--columns)) - var(--gap) + 2px + ((var(--gap) + 2px) / var(--columns)));
										className='carousel-item w-[calc(50%-30px+(30px/2))] md:w-[calc(33.333333%-30px+((30px)/3))] lg:w-[calc(25%-30px+((30px)/4))] first:ml-auto last:mr-auto'
									>
										<ProductCard
											product={product}
											itemListId={ga4?.item_list_id ?? 'product_shelf'}
											itemListName={ga4?.item_list_name ??
												stripHTML(title ?? '') ?? 'Prateleira'}
											isMobile={isMobile}
											index={index}
											isPerformanceTestPage={isPerformanceTestPage}
											disabledBuyMonthlySubscription={shouldDisabled}
										/>
									</Slider.Item>
								)
							})}
						</Slider>

						<div
							className={clsx(
								'z-[1] absolute top-1/2 left-0 -translate-y-1/2',
								products.length <= 4 && 'lg:hidden',
								products.length <= 3 && 'md:hidden',
								products.length <= 2 && 'sm:hidden',
								products.length <= 1 && 'hidden`',
							)}
						>
							<Slider.PrevButton className='btn btn-circle bg-opacity-80 text-salmon active:text-black active:bg-[#e1dfdb] active:bg-opacity-80 lg:hover:text-black bg-[#e1dfdb] lg:hover:bg-[#e1dfdb] lg:hover:bg-opacity-80 border-none'>
								<Icon size={24} id='ChevronLeft' strokeWidth={3} />
							</Slider.PrevButton>
						</div>
						<div
							className={clsx(
								'z-[1] absolute top-1/2 right-0 -translate-y-1/2',
								products.length <= 4 && 'lg:hidden',
								products.length <= 3 && 'md:hidden',
								products.length <= 2 && 'sm:hidden',
								products.length <= 1 && 'hidden`',
							)}
						>
							<Slider.NextButton className='btn btn-circle bg-opacity-80 text-salmon active:text-black active:bg-[#e1dfdb] active:bg-opacity-80 lg:hover:text-black bg-[#e1dfdb] lg:hover:bg-[#e1dfdb] lg:hover:bg-opacity-80 border-none'>
								<Icon size={24} id='ChevronRight' strokeWidth={3} />
							</Slider.NextButton>
						</div>

						<SliderJS rootId={id} infinite={infinite} />
					</div>
				)
				: (
					<div
						id={id}
						className='grid grid-cols-2 lg:grid-cols-5 gap-6 w-full justify-center'
					>
						{products?.map((product, index) => {
							const shouldDisabled = disabledBuyMonthlySubscription &&
								product.productID === MONTHLY_SKU_ID

							return (
								<div className='lg:max-w-[305px] h-full m-auto'>
									<ProductCard
										product={product}
										itemListId={ga4?.item_list_id ?? 'product_shelf'}
										itemListName={ga4?.item_list_name ??
											stripHTML(title ?? '') ?? 'Prateleira'}
										isMobile={isMobile}
										index={index}
										isPerformanceTestPage={isPerformanceTestPage}
										disabledBuyMonthlySubscription={shouldDisabled}
									/>
								</div>
							)
						})}
					</div>
				)}

			<SendEventOnView id={id} event={viewItemListEvent} />
		</div>
	)
}

export function LoadingFallback(p: unknown) {
	if (!isSchedulingActive((p as Props).scheduling)) {
		return null
	}

	const title = (p as Props).title
	const titleIcon = (p as Props).titleIcon
	const titleColor = (p as Props).titleColor
	return (
		<div className='w-full flex flex-col gap-12 mx-auto max-w-[1730px] py-6 lg:py-12 px-2 sm:px-4 min-h-[800px]'>
			<div className='flex items-center justify-center'>
				{title && <Title title={title} icon={titleIcon} color={titleColor} />}
			</div>

			<div className='animate-pulse grid h-fit grid-cols-2 lg:grid-cols-5 gap-6 items-center sm:gap-[30px] sm:px-4 md:px-[10px] md:gap-5 lg:px-[15px] lg:gap-8 mt-4 px-[5vw] min-h-[700px]'>
				<ProductCardLoadingFallback />
				<ProductCardLoadingFallback />

				<ProductCardLoadingFallback className='hidden lg:block' />
				<ProductCardLoadingFallback className='hidden lg:block' />
				<ProductCardLoadingFallback className='hidden lg:block' />
			</div>
		</div>
	)
}

export default ProductShelf
